import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import {
  Trash2, Edit3, RotateCcw, XCircle, ShieldAlert, Search, Filter,
  CheckSquare, Square, Music2, X,
} from "lucide-react";
import TagChipsInput from "./TagChipsInput";
import { MAIN_GENRES, STYLE_TAG_SUGGESTIONS, MOOD_TAG_SUGGESTIONS } from "../lib/taxonomy";

const STATIONS_FALLBACK = ["truth-hiphop", "worship", "study-instrumentals", "sabbath-flow"];
const CATEGORIES = ["music", "worship", "instrumental", "sermon", "teaching", "study"];

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return s.slice(0, 10); }
}

function StatusBadge({ status }) {
  const cls = status === "approved"
    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
    : status === "rejected"
    ? "text-red-400 bg-red-500/10 border-red-500/20"
    : "text-[#C5A059] bg-[#C5A059]/10 border-[#C5A059]/20";
  return <span className={`overline px-2 py-0.5 rounded-full border text-[10px] ${cls}`}>{status}</span>;
}

function ConfirmModal({ open, title, message, onConfirm, onCancel, requireType, busy }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  if (!open) return null;
  const canConfirm = !requireType || typed === requireType;
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 backdrop-blur-sm" data-testid="confirm-modal">
      <div className="card-surface p-6 max-w-md w-[90%] space-y-4 border-red-500/20">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <h3 className="font-serif text-xl text-[#FDFBF7]">{title}</h3>
            <p className="text-[#A1A4AB] text-sm mt-1">{message}</p>
          </div>
        </div>
        {requireType && (
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">
              Type <span className="text-red-300 font-mono">{requireType}</span> to confirm
            </label>
            <input
              autoFocus
              className="input-lux"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={requireType}
              data-testid="confirm-modal-type-input"
            />
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="btn-ghost text-sm" disabled={busy} data-testid="confirm-modal-cancel">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm || busy}
            data-testid="confirm-modal-confirm"
            className="px-4 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? "Working…" : "Permanently delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ track, stationOptions, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: track.title || "",
    artist: track.artist || "",
    category: track.category || "music",
    station: track.station || "",
    genre: track.genre || "",
    main_genre: track.main_genre || "",
    mood: track.mood || "",
    energy: track.energy || 3,
    in_rotation: !!track.in_rotation,
    featured: !!track.featured,
  });
  const [styleTags, setStyleTags] = useState(track.style_tags || []);
  const [moodTags, setMoodTags] = useState(track.mood_tags || []);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverBust, setCoverBust] = useState(track.cover_updated_at || track.created_at || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Local preview when admin picks a new cover
  useEffect(() => {
    if (!coverFile) { setCoverPreview(null); return; }
    const url = URL.createObjectURL(coverFile);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [coverFile]);

  const onPickCover = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setCoverFile(null); return; }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      setErr("Cover must be JPG, PNG, or WebP."); e.target.value = ""; return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setErr("Cover too large (max 8 MB)."); e.target.value = ""; return;
    }
    setErr("");
    setCoverFile(f);
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = {
        title: form.title.trim(),
        artist: form.artist.trim(),
        category: form.category,
        station: form.station || null,
        genre: form.genre || null,
        main_genre: form.main_genre || null,
        style_tags: styleTags,
        mood_tags: moodTags,
        mood: form.mood || null,
        energy: parseInt(form.energy, 10) || null,
        in_rotation: form.in_rotation,
        featured: form.featured,
      };
      // PATCH metadata first — this NEVER changes audio_url, so if the track is
      // currently playing the audio source stays untouched.
      await api.patch(`/admin/tracks/${track.id}`, body);
      // Cover is a separate POST so we can swap art without recreating the track.
      // The audio element is unaffected; only TrackCards re-fetch on next paint.
      if (coverFile) {
        const fd = new FormData();
        fd.append("cover", coverFile);
        const { data } = await api.post(`/admin/tracks/${track.id}/cover`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setCoverBust(data?.created_at || Date.now());
      }
      onSaved?.();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const existingCoverUrl = coverUrl(track) ? `${coverUrl(track)}?v=${encodeURIComponent(coverBust || "")}` : null;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 backdrop-blur-sm" data-testid="edit-modal" onClick={onClose}>
      <div
        className="card-surface p-6 max-w-2xl w-[94%] max-h-[92vh] overflow-y-auto space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 sticky top-0 bg-[#0E1014] -m-6 p-6 mb-0 z-10 border-b border-[#22262E]">
          <Edit3 className="w-5 h-5 text-[#C5A059] mt-0.5" strokeWidth={1.5} />
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-xl text-[#FDFBF7] truncate">Edit “{track.title}”</h3>
            <p className="text-[#A1A4AB] text-xs">Changes save instantly. Audio stream URL never changes — playback is not interrupted.</p>
          </div>
          <button onClick={onClose} className="text-[#A1A4AB] hover:text-[#FDFBF7]" aria-label="Close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Cover art row */}
        <div className="grid md:grid-cols-[120px_1fr] gap-4 items-start">
          <div className="w-28 h-28 rounded-md overflow-hidden border border-[#22262E] bg-[#111317] relative" data-testid="edit-cover-preview">
            {coverPreview ? (
              <img src={coverPreview} alt="" className="w-full h-full object-cover" />
            ) : existingCoverUrl ? (
              <img src={existingCoverUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 grid place-items-center text-[#6C707A] text-xs">No cover</div>
            )}
          </div>
          <div className="flex-1">
            <label className="text-xs overline text-[#6C707A] mb-1 block">Replace cover (JPG/PNG/WebP, ≤ 8 MB)</label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={onPickCover}
              className="input-lux"
              data-testid="edit-cover-input"
            />
            {coverFile && (
              <div className="text-xs text-[#A1A4AB] mt-1.5">
                Selected: {coverFile.name} · {(coverFile.size / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Title *</label>
            <input className="input-lux" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title" />
          </div>
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Artist *</label>
            <input className="input-lux" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} data-testid="edit-artist" />
          </div>
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Category</label>
            <select className="input-lux" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="edit-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Station</label>
            <select className="input-lux" value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} data-testid="edit-station">
              <option value="">— none —</option>
              {stationOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Main genre</label>
            <select className="input-lux" value={form.main_genre} onChange={(e) => setForm({ ...form, main_genre: e.target.value })} data-testid="edit-main-genre">
              <option value="">— none —</option>
              {MAIN_GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Genre (free text — preserved)</label>
            <input className="input-lux" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} data-testid="edit-genre" />
          </div>
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Mood (legacy single)</label>
            <input className="input-lux" value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })} data-testid="edit-mood" />
          </div>
          <div>
            <label className="text-xs overline text-[#6C707A] mb-1 block">Energy (1–5)</label>
            <input type="number" min="1" max="5" className="input-lux" value={form.energy} onChange={(e) => setForm({ ...form, energy: e.target.value })} data-testid="edit-energy" />
          </div>
        </div>

        <TagChipsInput
          label="Style / subgenre tags"
          value={styleTags}
          onChange={setStyleTags}
          suggestions={STYLE_TAG_SUGGESTIONS}
          placeholder="Add style tags…"
          testId="edit-style-tags"
        />
        <TagChipsInput
          label="Mood tags (canonical)"
          value={moodTags}
          onChange={setMoodTags}
          suggestions={MOOD_TAG_SUGGESTIONS}
          placeholder="Add mood tags…"
          testId="edit-mood-tags"
          maxItems={10}
        />

        <div className="flex items-center gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm text-[#A1A4AB]">
            <input type="checkbox" className="accent-[#C5A059]" checked={form.in_rotation} onChange={(e) => setForm({ ...form, in_rotation: e.target.checked })} data-testid="edit-in-rotation" /> In rotation
          </label>
          <label className="flex items-center gap-2 text-sm text-[#A1A4AB]">
            <input type="checkbox" className="accent-[#C5A059]" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="edit-featured" /> Featured
          </label>
        </div>

        {err && <div className="text-red-400 text-sm">{err}</div>}
        <div className="flex gap-2 justify-end pt-2 sticky bottom-0 bg-[#0E1014] -m-6 p-6 mt-0 border-t border-[#22262E]">
          <button onClick={onClose} className="btn-ghost text-sm" disabled={busy}>Cancel</button>
          <button onClick={save} disabled={busy || !form.title.trim() || !form.artist.trim()} className="btn-primary text-sm" data-testid="edit-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TrackManagement() {
  const [tracks, setTracks] = useState([]);
  const [stationOptions, setStationOptions] = useState(STATIONS_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // { mode: 'single'|'bulk', ids: [] }
  const [editing, setEditing] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/tracks/all");
      setTracks(data || []);
    } catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Fetch stations once so the Edit dropdown stays in sync with backend.
  // Filters out dynamic stations (those have no static station tag).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stations");
        const dynamic = new Set(["new-submissions", "most-liked", "recently-approved"]);
        const ids = (data || []).map((s) => s.id).filter((id) => !dynamic.has(id));
        if (ids.length) setStationOptions(ids);
      } catch { /* fallback already set */ }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tracks.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (!q) return true;
      return (t.title || "").toLowerCase().includes(q)
        || (t.artist || "").toLowerCase().includes(q)
        || (t.uploader_name || "").toLowerCase().includes(q)
        || (t.station || "").toLowerCase().includes(q);
    });
  }, [tracks, query, statusFilter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = filtered.every((t) => next.has(t.id));
      if (allSelected) filtered.forEach((t) => next.delete(t.id));
      else filtered.forEach((t) => next.add(t.id));
      return next;
    });
  };
  const selectAllTestTracks = () => {
    const testIds = tracks.filter((t) =>
      t.is_test || (t.source === "e2e-test")
      || (t.title || "").toLowerCase().includes("test")
      || (t.artist || "").toLowerCase().includes("test")
    ).map((t) => t.id);
    setSelected(new Set(testIds));
  };

  const requestSingleDelete = (track) => {
    setConfirm({
      mode: "single",
      ids: [track.id],
      title: `Delete “${track.title}”?`,
      message: "This will permanently remove this track and its audio file. Reactions, favorites, and playlist entries will also be cleared.",
      requireType: null,
    });
  };

  const requestBulkDelete = () => {
    if (selected.size === 0) return;
    setConfirm({
      mode: "bulk",
      ids: Array.from(selected),
      title: `Delete ${selected.size} tracks?`,
      message: "This will permanently remove the selected tracks, their audio/cover files, reactions, favorites, and playlist entries. This cannot be undone.",
      requireType: "DELETE",
    });
  };

  const performDelete = async () => {
    if (!confirm) return;
    setActionBusy(true); setErr("");
    try {
      if (confirm.mode === "single") {
        await api.delete(`/admin/tracks/${confirm.ids[0]}`);
      } else {
        await api.post("/admin/tracks/bulk-delete", { track_ids: confirm.ids, confirm: "DELETE" });
      }
      setSelected(new Set());
      setConfirm(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); } finally { setActionBusy(false); }
  };

  const unapprove = async (track) => {
    setActionBusy(true); setErr("");
    try {
      await api.post(`/admin/tracks/${track.id}/unapprove`);
      await load();
    } catch (e) { setErr(formatApiError(e)); } finally { setActionBusy(false); }
  };

  const reject = async (track) => {
    const note = window.prompt("Rejection note (visible to the uploader):");
    if (note == null || note.trim() === "") return;
    setActionBusy(true); setErr("");
    try {
      await api.post(`/admin/submissions/${track.id}/reject`, { admin_notes: note });
      await load();
    } catch (e) { setErr(formatApiError(e)); } finally { setActionBusy(false); }
  };

  return (
    <section className="space-y-4" data-testid="track-management-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight flex items-center gap-2">
          <Music2 className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} /> Track management
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#6C707A] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              className="input-lux pl-9 w-56" placeholder="Search title / artist / station"
              value={query} onChange={(e) => setQuery(e.target.value)}
              data-testid="track-mgmt-search"
            />
          </div>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-[#6C707A]" strokeWidth={1.5} />
            <select className="input-lux py-1.5" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="track-mgmt-status-filter">
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 px-1">
        <div className="text-sm text-[#A1A4AB]">
          <span className="text-[#FDFBF7] font-medium">{filtered.length}</span> shown
          {" · "}
          <span className="text-[#FDFBF7] font-medium">{selected.size}</span> selected
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={selectAllTestTracks} className="btn-ghost text-xs" data-testid="track-mgmt-select-tests">
            Select all test tracks
          </button>
          <button
            onClick={requestBulkDelete}
            disabled={selected.size === 0 || actionBusy}
            className="px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-medium hover:bg-red-500/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="track-mgmt-bulk-delete"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete selected ({selected.size})
          </button>
        </div>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      <div className="card-surface p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="track-mgmt-table">
            <thead className="bg-[#0E1014] border-b border-[#22262E]">
              <tr className="text-left text-[10px] overline text-[#6C707A]">
                <th className="px-3 py-2 w-8">
                  <button onClick={toggleAllVisible} aria-label="Select all visible" data-testid="track-mgmt-select-all-visible">
                    {allFilteredSelected ? <CheckSquare className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} /> : <Square className="w-4 h-4" strokeWidth={1.5} />}
                  </button>
                </th>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Artist</th>
                <th className="px-3 py-2">Station</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Plays</th>
                <th className="px-3 py-2 text-right">Likes</th>
                <th className="px-3 py-2 text-right">Dislikes</th>
                <th className="px-3 py-2">Audio</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}><td colSpan={11} className="px-3 py-4"><div className="h-6 rounded shimmer" /></td></tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-[#6C707A]">No tracks match.</td></tr>
              ) : filtered.map((t) => {
                const isTest = t.is_test || t.source === "e2e-test"
                  || (t.title || "").toLowerCase().includes("test")
                  || (t.artist || "").toLowerCase().includes("test");
                return (
                  <tr key={t.id} className={`border-b border-[#1A1D24] hover:bg-white/[0.02] ${selected.has(t.id) ? "bg-[#C5A059]/5" : ""}`} data-testid={`track-row-${t.id}`}>
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggleOne(t.id)} aria-label="Select" data-testid={`track-row-select-${t.id}`}>
                        {selected.has(t.id) ? <CheckSquare className="w-4 h-4 text-[#C5A059]" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-[#6C707A]" strokeWidth={1.5} />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      <div className="text-[#FDFBF7] truncate">{t.title}</div>
                      {isTest && <div className="overline text-[10px] text-amber-400/80 mt-0.5">Test</div>}
                    </td>
                    <td className="px-3 py-2.5 text-[#A1A4AB] max-w-[180px] truncate">{t.artist}</td>
                    <td className="px-3 py-2.5 text-[#A1A4AB]">{t.station || "—"}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={t.status} /></td>
                    <td className="px-3 py-2.5 text-[#A1A4AB] whitespace-nowrap">{fmtDate(t.created_at)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-[#FDFBF7]">{t.play_count || 0}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-emerald-400">{t.like_count || 0}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-red-400">{t.dislike_count || 0}</td>
                    <td className="px-3 py-2.5">
                      {t.audio_storage_path ? (
                        <span className="overline text-[10px] text-emerald-400">stored</span>
                      ) : (
                        <span className="overline text-[10px] text-red-400">missing</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditing(t)} title="Edit" data-testid={`track-row-edit-${t.id}`}
                                className="p-1.5 rounded-md text-[#A1A4AB] hover:text-[#C5A059] hover:bg-[#C5A059]/10">
                          <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        {t.status === "approved" && (
                          <button onClick={() => unapprove(t)} title="Unapprove → pending" data-testid={`track-row-unapprove-${t.id}`}
                                  className="p-1.5 rounded-md text-[#A1A4AB] hover:text-[#C5A059] hover:bg-[#C5A059]/10">
                            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        )}
                        {t.status !== "rejected" && (
                          <button onClick={() => reject(t)} title="Reject" data-testid={`track-row-reject-${t.id}`}
                                  className="p-1.5 rounded-md text-[#A1A4AB] hover:text-red-300 hover:bg-red-500/10">
                            <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        )}
                        <button onClick={() => requestSingleDelete(t)} title="Delete permanently" data-testid={`track-row-delete-${t.id}`}
                                className="p-1.5 rounded-md text-[#A1A4AB] hover:text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title || ""}
        message={confirm?.message || ""}
        requireType={confirm?.requireType}
        busy={actionBusy}
        onCancel={() => setConfirm(null)}
        onConfirm={performDelete}
      />

      {editing && (
        <EditModal
          track={editing}
          stationOptions={stationOptions}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}
